import isEmpty from "lodash/isEmpty";
import debounce from "lodash/debounce";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Popup } from "semantic-ui-react";

import { useGetCustomerQuery } from "../../api/customerApi";
import {
  MachineTranslateTaskQueue,
  DoneTranslateTextResponse,
  detectLanguage,
} from "../../api/machineTranslationApi";
import { Button, Icon, Toggle } from "../../components/tailwind";
import { Text } from "../../components/Text";
import { TranslateButton } from "../../components/TranslateButton";
import {
  LanguageCode,
  SUICustomerLanguageDropdown,
  renderLanguageFromCode,
} from "../../customers/customerlanguages";
import { RootState } from "../../utils/store";
import { PreviouslyMachineTranslatedRail } from "../previously-machine-translated-resources/PreviouslyMachineTranslatedRail";
import {
  ProofreadingInfo,
  Translation,
  TranslationInfo,
} from "../previously-machine-translated-resources/PreviouslyMachineTranslatedRailList";
import { FindAndReplaceModal } from "../FindAndReplaceModal";
import { popupDelay } from "../../customers/gpt/types";
import { createHref } from "../../utils/hrefUtils";
import { MachineTranslateTextEmptyResults } from "../MachineTranslateTextEmptyResults";
import { MachineTranslateTextResults } from "../MachineTranslateTextResults";
import { useGetAuthQuery } from "../../api/authApi";
import {
  ProofreadRequestModal,
  Props as ProofreadRequestModalProps,
} from "../ProofreadRequestModal";
import { NoTranslateTextarea } from "../NoTranslateTextarea";

export type TaskStatus = {
  loading: boolean;
  error: unknown;
  result: DoneTranslateTextResponse;
};

export type OverwrittenTextInfo = {
  text: string;
  translationEventId: number;
};

export const MachineTranslateText: React.FC = () => {
  const { data: customer } = useGetCustomerQuery();
  const { data: auth } = useGetAuthQuery();
  const token = useSelector((state: RootState) => state.auth.token);

  const [
    selectAllTargetLanguagesClicked,
    setSelectAllTargetLanguagesClicked,
  ] = useState(false);

  const [
    languagesSentToProofreading,
    setLanguagesSentToProofreading,
  ] = useState<LanguageCode[]>([]);

  const machineTranslateTaskQueue = useMemo(() => {
    if (token)
      return new MachineTranslateTaskQueue<DoneTranslateTextResponse>(
        token,
        2000
      );
  }, [token]);
  const [autoSearchPrevTranslations, setAutoSearchPrevTranslations] = useState(
    false
  );
  const [sourceText, setSourceText] = useState<string>("");

  const [sourceContext, setSourceContext] = useState<string>("");

  const [selectedSourceLanguage, setSelectedSourceLanguage] = useState<
    LanguageCode
  >(null);

  const [
    detectedSourceLanguage,
    setDetectedSourceLanguage,
  ] = useState<LanguageCode | null>(null);

  const [selectedTargetLanguages, setSelectedTargetLanguages] = useState<
    LanguageCode[]
  >([]);

  const [proofreadingRequests, setProofreadingRequests] = useState<
    { [key in LanguageCode]?: ProofreadingInfo }
  >({});
  const [overwrittenTexts, setOverwrittenTexts] = useState<
    { [key in LanguageCode]?: OverwrittenTextInfo }
  >({});

  const [viewingProofreading, setViewingProofreading] = useState<
    LanguageCode[]
  >([]);

  const [FindAndReplaceModalOpen, setFindAndReplaceModalOpen] = useState<
    boolean
  >(false);

  const [translateStatus, setTranslateStatus] = useState<
    {
      [key in LanguageCode]?: TaskStatus;
    }
  >({});
  const [refreshTodaysTranslations, setRefreshTodaysTranslations] = useState(
    false
  );

  const debouncedDetectLanguage = useCallback(
    debounce(async (text: string, token: string) => {
      if (!text || text.trim().length < 4) {
        setDetectedSourceLanguage(null);
        return;
      }

      try {
        const detectedLanguage = await detectLanguage(token, text);
        setDetectedSourceLanguage(detectedLanguage);
      } catch (error) {
        console.error("Language detection failed:", error);
        setDetectedSourceLanguage(null);
      }
    }, 1000),
    []
  );

  useEffect(() => {
    if (!selectedSourceLanguage && sourceText) {
      debouncedDetectLanguage(sourceText, token);
    } else if (!sourceText) {
      setDetectedSourceLanguage(null);
    }
  }, [sourceText, selectedSourceLanguage, token, debouncedDetectLanguage]);

  const haveTranslationJobs = useMemo((): boolean => {
    return Object.keys(translateStatus).length > 0;
  }, [translateStatus]);

  const loadPreviousTranslation = (info: TranslationInfo): void => {
    setViewingProofreading([]);
    setProofreadingRequests({});
    setSourceText(info.source_text);
    setSourceContext(info.source_context);
    setSelectedSourceLanguage(info.source_language);
    setSelectedTargetLanguages([]);
    setTranslateStatus({});
    setOverwrittenTexts({});
    (Object.entries(info.translations) as [
      LanguageCode,
      Translation
    ][]).forEach(([language, translation]) => {
      setSelectedTargetLanguages((prev) => [...prev, language]);
      if (translation.proofreading_info) {
        setProofreadingRequests((prev) => ({
          ...prev,
          [language]: translation.proofreading_info,
        }));
        if (translation.proofreading_info.done) {
          setViewingProofreading((prev) => [...prev, language]);
        }
      }
      setOverwrittenTexts((prev) => ({
        ...prev,
        [language]: {
          text: translation.overwritten_text,
          translationEventId: translation.translation_event_id,
        },
      }));
      setTranslateStatus((prev) => ({
        ...prev,
        [language]: {
          loading: false,
          error: null,
          result: {
            translated_text: translation.translated_text,
            translation_event_id: translation.translation_event_id,
          },
        },
      }));
    });
  };

  const startTranslate = async (configGroupId?: number): Promise<void> => {
    setLanguagesSentToProofreading([]);

    const sourceLanguageToUse =
      selectedSourceLanguage || detectedSourceLanguage;

    if (!sourceLanguageToUse) {
      return;
    }

    return machineTranslateTaskQueue
      .translateText(
        sourceText,
        sourceContext,
        sourceLanguageToUse,
        selectedTargetLanguages,
        configGroupId
      )
      .then((source_language: LanguageCode) => {
        setSelectedSourceLanguage(source_language);

        const translateStatusObj: {
          [language: string]: TaskStatus;
        } = {};
        selectedTargetLanguages.forEach((language) => {
          translateStatusObj[language] = {
            loading: true,
            error: null,
            result: null,
          };
        });
        setTranslateStatus(translateStatusObj);

        selectedTargetLanguages.forEach((language) => {
          machineTranslateTaskQueue
            .waitUntilTranslateLanguageIsDone(language)
            .then((result) => {
              setRefreshTodaysTranslations(true);
              setTranslateStatus((prev) => ({
                ...prev,
                [language]: {
                  loading: false,
                  error: null,
                  result: result,
                },
              }));
            })
            .catch(() => {
              setTranslateStatus((prev) => ({
                ...prev,
                [language]: {
                  loading: false,
                  error:
                    language == source_language
                      ? "Translation failed: Source language & target language are the same"
                      : "Translation failed",
                  result: null,
                },
              }));
            });
        });
      });
  };

  const allTranslateJobsDone = useMemo((): boolean => {
    return Object.values(translateStatus).every((status) => !status.loading);
  }, [translateStatus]);

  const handleReset = (): void => {
    setSourceText("");
    setSourceContext("");
    setTranslateStatus({});
    setViewingProofreading([]);
    setProofreadingRequests({});
    setSelectedSourceLanguage(null);
    setSelectedTargetLanguages([]);
    setLanguagesSentToProofreading([]);
  };

  const showClearIcon = useMemo(
    () =>
      sourceText ||
      sourceContext ||
      !!Object.keys(translateStatus).length ||
      selectedSourceLanguage ||
      !!selectedTargetLanguages.length,
    [sourceText, sourceContext, translateStatus]
  );

  const allIsSelected = useMemo((): boolean => {
    if (!customer) return false;
    let allLanguagesLength = customer.languages.length;
    if (selectedSourceLanguage) {
      allLanguagesLength--;
    }
    return selectedTargetLanguages.length === allLanguagesLength;
  }, [selectedTargetLanguages, customer, selectedSourceLanguage]);

  const allSelectedTargetLanguagesHasProofreadingRequest = useMemo(
    (): boolean =>
      selectedTargetLanguages.every(
        (language) => proofreadingRequests?.[language]
      ),
    [proofreadingRequests, selectedTargetLanguages]
  );

  const languagesNotSendToProofreading = useMemo(
    (): LanguageCode[] =>
      selectedTargetLanguages.filter(
        (language) => !proofreadingRequests?.[language]
      ),
    [proofreadingRequests, selectedTargetLanguages]
  );

  const proofreadingModalButtonProps = useMemo((): ProofreadRequestModalProps["buttonProps"] => {
    const allProofreadingRequestsSent =
      allSelectedTargetLanguagesHasProofreadingRequest ||
      languagesSentToProofreading.length === selectedTargetLanguages.length;

    const someLanguagesHaveProofreadingRequests = Object.values(
      proofreadingRequests
    ).some(Boolean);

    const common = {
      compact: true,
      size: "sm",
      className: "tw-float-end",
      disabled: !allTranslateJobsDone || allProofreadingRequestsSent,
    } as const;

    let content = "Send all for proofreading...";
    let variant: ProofreadRequestModalProps["buttonProps"]["variant"] =
      "primary";

    if (someLanguagesHaveProofreadingRequests) {
      content = "Send remaining languages for proofreading...";
    }
    if (allProofreadingRequestsSent) {
      content = "All translations sent for proofreading";
      variant = "secondary-alt";
    }

    return {
      ...common,
      content,
      variant,
    };
  }, [
    allTranslateJobsDone,
    allSelectedTargetLanguagesHasProofreadingRequest,
    languagesSentToProofreading,
  ]);

  const onSendToProofreadingCallback = (
    jobIdsByLanguage?: { [key in LanguageCode]?: string }
  ): void => {
    setRefreshTodaysTranslations(true);
    if (jobIdsByLanguage) {
      Object.entries(jobIdsByLanguage).forEach(([language, jobId]) => {
        setProofreadingRequests((prev) => ({
          ...prev,
          [language]: {
            done: false,
            job_id: jobId,
          },
        }));
      });
    }
  };

  const sourcePlaceholder = useMemo(() => {
    if (detectedSourceLanguage && !selectedSourceLanguage) {
      const languageName = renderLanguageFromCode(
        detectedSourceLanguage,
        customer?.languages ?? [],
        true,
        true
      );
      if (languageName) return `${languageName} - identified`;
    }
    return "Identify language";
  }, [detectedSourceLanguage, selectedSourceLanguage, customer]);

  const detectedLanguageOption = useMemo(() => {
    if (!detectedSourceLanguage) return null;
    const languageName = renderLanguageFromCode(
      detectedSourceLanguage,
      customer?.languages ?? [],
      true,
      true
    );
    return {
      key: detectedSourceLanguage,
      value: detectedSourceLanguage,
      text: <span>{languageName} - identified</span>,
    };
  }, [detectedSourceLanguage, selectedSourceLanguage, customer]);

  return (
    <>
      <PreviouslyMachineTranslatedRail
        loadPreviousTranslation={loadPreviousTranslation}
        refreshTodaysTranslations={refreshTodaysTranslations}
        setRefreshTodaysTranslations={setRefreshTodaysTranslations}
      />
      <div className="tw-grid tw-grid-cols-[1fr_auto_1fr] tw-gap-8">
        <div>
          <SUICustomerLanguageDropdown
            clearable
            fluid
            data-testid="machine-translate-text-source-dropdown"
            selectOnBlur={false}
            selectOnNavigation={false}
            additionalOption={detectedLanguageOption}
            onChange={(e, { value }): void => {
              setSelectedSourceLanguage(value as LanguageCode);
              if (value) {
                setDetectedSourceLanguage(null);
              }

              setSelectedTargetLanguages((prev) => {
                const langCodes = selectAllTargetLanguagesClicked
                  ? customer.languages.map((lang) => lang.code)
                  : prev;
                if (langCodes.includes(value as LanguageCode)) {
                  return langCodes.filter((lang) => lang !== value);
                }
                return langCodes;
              });
            }}
            placeholder={sourcePlaceholder}
            selection
            value={selectedSourceLanguage}
          />
        </div>
        <div className="tw-pt-2">
          <Icon name="arrow_forward" />
        </div>
        <div className="tw-flex tw-flex-row tw-gap-2">
          <SUICustomerLanguageDropdown
            clearable
            fluid
            multiple
            disabledLanguageOption={
              selectedSourceLanguage || detectedSourceLanguage
            }
            data-testid="machine-translate-text-target-dropdown"
            onChange={(_, { value }): void => {
              setSelectedTargetLanguages(value as LanguageCode[]);
              setSelectAllTargetLanguagesClicked(false);
            }}
            placeholder="Select language(s)"
            selection
            value={selectedTargetLanguages}
            className="tw-place-self-start"
          />
          <Button
            content="Select All"
            className="tw-place-self-start"
            compact
            size="sm"
            data-testid="machine-translate-text-select-all-button"
            disabled={!customer || allIsSelected}
            variant="secondary-alt"
            onClick={(): void => {
              setSelectAllTargetLanguagesClicked(true);
              setSelectedTargetLanguages(
                customer.languages
                  .filter(
                    (language) => language.code !== selectedSourceLanguage
                  )
                  .map((language) => language.code)
              );
            }}
          />
        </div>
        <div className="-tw-mt-6">
          <div className="tw-relative">
            {showClearIcon && (
              <Popup
                size="tiny"
                content="Clear"
                mouseEnterDelay={popupDelay}
                trigger={
                  <span
                    onClick={handleReset}
                    className="tw-absolute tw-right-0 tw-h-[40px] tw-w-[40px] tw-cursor-pointer tw-grid tw-place-items-center tw-top-9"
                  >
                    <Icon name="close" />
                  </span>
                }
              />
            )}
            <NoTranslateTextarea
              value={sourceText}
              onChange={setSourceText}
              textareaClassName="!tw-min-h-52"
              testId="machine-translate-textarea"
            >
              <Toggle
                label="Auto search Previous Translations"
                checked={autoSearchPrevTranslations}
                setChecked={setAutoSearchPrevTranslations}
                data-testid="machine-translate-text-auto-search-toggle"
              />
            </NoTranslateTextarea>
          </div>
          <Text lessMargin>
            Additional context/Prompt instruction
            <span
              className="ui text small grey"
              style={{ marginInline: "5px" }}
            >
              (not translated)
            </span>
            <Popup
              wide
              trigger={
                <Icon name="help" className="tw-align-middle tw-text-lg" />
              }
            >
              When translating with GPT models, this is sent in as an additional
              prompt instruction. When translating with DeepL, this is a feature
              to provide additional context. For MT services not mentioned, this
              does nothing.
            </Popup>
          </Text>
          <textarea
            className="txu-textarea"
            data-testid="machine-translate-text-context-textarea"
            onChange={(e): void => setSourceContext(e.target.value)}
            value={sourceContext}
          />
          <div className="tw-flex tw-items-start tw-justify-end tw-gap-1">
            <FindAndReplaceModal
              open={FindAndReplaceModalOpen}
              setOpen={setFindAndReplaceModalOpen}
            />

            <div>
              {(auth?.credential?.role_is_superuser ||
                auth?.user?.is_staff) && (
                <>
                  <h5 className=" tw-text-gray-500 tw-mb-2">Text Control</h5>
                  <div className="tw-grid tw-grid-cols-[auto,_1fr] tw-items-start tw-mr-auto tw-gap-4 tw-gap-y-8 tw-text-pretty">
                    <Button
                      size="sm"
                      content="No-translate word list..."
                      variant="secondary-alt"
                      data-testid="machine-translate-text-no-translate-button"
                      onClick={(): void =>
                        window.openSubpage(
                          null,
                          createHref("noTranslateWordList", customer)
                        )
                      }
                    />
                    <p className="tw-m-0 tw-text-gray-500 tw-text-sm">
                      Specify words or terms which should never be translated,
                      use to preserve names or specific terminology.
                    </p>
                    <Button
                      size="sm"
                      content="Glossaries..."
                      variant="secondary-alt"
                      data-testid="machine-translate-text-glossaries-button"
                      onClick={(): void =>
                        window.openSubpage(
                          null,
                          createHref("glossaries", customer)
                        )
                      }
                    />
                    <p className="tw-m-0 tw-text-gray-500 tw-text-sm">
                      Specify how words and terms should be translated in a
                      specific language pair.
                    </p>
                    <Button
                      size="sm"
                      content="Word Replacements..."
                      variant="secondary-alt"
                      data-testid="machine-translate-text-word-replacements-button"
                      onClick={(): void => setFindAndReplaceModalOpen(true)}
                    />
                    <p className="tw-m-0 tw-text-gray-500 tw-text-sm">
                      Automatically apply find and replace rules on the final
                      text as post-processing.
                    </p>
                  </div>
                </>
              )}
            </div>

            <TranslateButton
              disabled={
                isEmpty(selectedTargetLanguages) ||
                isEmpty(sourceText) ||
                selectedSourceLanguage === selectedTargetLanguages[0] ||
                !allTranslateJobsDone
              }
              loading={haveTranslationJobs && !allTranslateJobsDone}
              variant="primary"
              data-testid="machine-translate-text-translate-button"
              onClick={(configGroupId): void => {
                startTranslate(configGroupId);
              }}
            >
              Translate
            </TranslateButton>
          </div>
        </div>
        <div />
        <div>
          {!haveTranslationJobs ? (
            <MachineTranslateTextEmptyResults
              sourceText={sourceText}
              selectedSourceLanguage={selectedSourceLanguage}
              autoSearchPrevTranslations={autoSearchPrevTranslations}
              loadPreviousTranslation={loadPreviousTranslation}
            />
          ) : (
            <>
              <ProofreadRequestModal
                proofreadingRequest={{
                  original: {
                    text: sourceText,
                    language: selectedSourceLanguage,
                  },
                  requests: selectedTargetLanguages
                    .filter(
                      (language) =>
                        translateStatus?.[language] &&
                        !translateStatus[language].error &&
                        languagesNotSendToProofreading.includes(language)
                    )
                    .map((language) => ({
                      text: translateStatus[language]?.result?.translated_text,
                      language: language,
                    })),
                }}
                buttonProps={proofreadingModalButtonProps}
                onSendToProofreadingCallback={onSendToProofreadingCallback}
              />
              <MachineTranslateTextResults
                translateStatus={translateStatus}
                setTranslateStatus={setTranslateStatus}
                viewingProofreading={viewingProofreading}
                setViewingProofreading={setViewingProofreading}
                proofreadingRequests={proofreadingRequests}
                onSendToProofreadingCallback={onSendToProofreadingCallback}
                overwrittenTexts={overwrittenTexts}
                sourceText={sourceText}
                selectedSourceLanguage={selectedSourceLanguage}
                setRefreshTodaysTranslations={setRefreshTodaysTranslations}
                allTranslateJobsDone={allTranslateJobsDone}
                machineTranslateTaskQueue={machineTranslateTaskQueue}
              />
            </>
          )}
        </div>
      </div>
    </>
  );
};
